import React, { useState, useEffect, useCallback } from 'react';
import { TrackedObject } from '../services/ObjectsService';
import objectsService from '../services/ObjectsService';

export interface ReAuthModalProps {
  object: TrackedObject;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'CREDENTIALS' | 'LOADING' | 'SMS_CODE' | 'SUCCESS' | 'ERROR';

const ReAuthModal: React.FC<ReAuthModalProps> = ({ object, onClose, onSuccess }) => {
  const hasStoredCredentials = !!object.encryptedUsername;

  const [step, setStep] = useState<ModalStep>(hasStoredCredentials ? 'LOADING' : 'CREDENTIALS');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [session, setSession] = useState('');
  const [challengeUsername, setChallengeUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInitiate = useCallback(async (user?: string, pass?: string) => {
    setLoading(true);
    setStep('LOADING');
    try {
      const response = await objectsService.initiateReAuth(object.id, user, pass);
      setSession(response.session);
      if ((response as any).username) {
        setChallengeUsername((response as any).username);
      } else if (user) {
        setChallengeUsername(user);
      }
      setStep('SMS_CODE');
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to initiate re-authentication';
      setError(message);
      setStep('ERROR');
    } finally {
      setLoading(false);
    }
  }, [object.id]);

  // Auto-initiate when object has stored credentials
  useEffect(() => {
    if (hasStoredCredentials) {
      handleInitiate();
    }
  }, [hasStoredCredentials, handleInitiate]);

  const handleCredentialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleInitiate(username, password);
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await objectsService.respondReAuth(object.id, smsCode, session, challengeUsername);
      setStep('SUCCESS');
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to verify SMS code';
      setError(message);
      setStep('ERROR');
    } finally {
      setLoading(false);
    }
  };

  // Auto-close after success
  useEffect(() => {
    if (step === 'SUCCESS') {
      const timer = setTimeout(() => {
        onSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [step, onSuccess]);

  const handleRetry = () => {
    setError('');
    setSmsCode('');
    if (hasStoredCredentials) {
      handleInitiate();
    } else {
      setStep('CREDENTIALS');
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'CREDENTIALS':
        return (
          <form onSubmit={handleCredentialSubmit}>
            <div className="form-group">
              <label htmlFor="reauth-username">Username</label>
              <input
                id="reauth-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Sidewalk username"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="reauth-password">Password</label>
              <input
                id="reauth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sidewalk password"
                disabled={loading}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                Submit
              </button>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        );

      case 'LOADING':
        return (
          <div className="loading-spinner">Authenticating...</div>
        );

      case 'SMS_CODE':
        return (
          <form onSubmit={handleVerify}>
            <p>An SMS code has been sent to your phone. Enter it below to complete re-authentication.</p>
            <div className="form-group">
              <label htmlFor="reauth-smscode">SMS Code</label>
              <input
                id="reauth-smscode"
                type="text"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                placeholder="Enter SMS code"
                disabled={loading}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        );

      case 'SUCCESS':
        return (
          <div className="message success">
            Re-authentication successful! This dialog will close automatically.
          </div>
        );

      case 'ERROR':
        return (
          <div>
            <div className="message error">{error}</div>
            <div className="form-actions">
              <button type="button" className="btn-primary" onClick={handleRetry}>
                Retry
              </button>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h3>Re-Authenticate: {object.name}</h3>
        {renderContent()}
      </div>
    </div>
  );
};

export default ReAuthModal;
